/**
 * Veranstaltungen: Liste/Kalender-Umschalter, Filter-Tabs + "Event anlegen"-Drawer
 * mit Kunden-Select (Stitch: maestro_veranstaltungen). Detailseite: pages/event-detail.tsx.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import type { Customer, EventRow } from "../ui/types";
import { EVENT_STATUS, formatDate, money } from "../ui/format";
import {
  Drawer,
  EmptyState,
  ErrorNote,
  Icon,
  LoadingRows,
  PageHeader,
  StatusPill,
} from "../ui/components";

const FILTERS: { key: string; label: string }[] = [
  { key: "all", label: "Alle" },
  { key: "open", label: "Offen" },
  { key: "offer_sent", label: "Angebot gesendet" },
  // CX-Audit 2026-07-17: Sammel-Tab wie „Offen" — Ziel des Dashboard-Funnels „Gewonnen"
  // (offer_chosen + paid + completed; Überlappung mit Bezahlt/Durchgeführt ist gewollt,
  // dasselbe Muster wie Offen ⊇ Angebot gesendet).
  { key: "won", label: "Gewonnen" },
  { key: "paid", label: "Bezahlt" },
  { key: "completed", label: "Durchgeführt" },
];

const OPEN = new Set(["inquiry", "offer_draft", "offer_sent", "offer_chosen"]);
const CONFIRMED = new Set(["offer_chosen", "paid", "completed"]);

export default function EventsPage() {
  const { data, loading, error, reload } = useApi<EventRow[]>("/api/events");
  const customersQ = useApi<Customer[]>("/api/customers");
  const navigate = useNavigate();
  // H4-Folge: "Angebot erstellen" von der Kunden-Vollseite oeffnet diese Drawer vorbefuellt.
  const [params, setParams] = useSearchParams();
  // CX-Audit 2026-07-17: Filter aus der URL lesbar/setzbar (?filter=offer_sent etc.) —
  // damit koennen Dashboard-KPIs gezielt in die passend gefilterte Liste verlinken und
  // der Zustand ueberlebt Reload/Zurueck-Navigation.
  const urlFilter = params.get("filter");
  const filter = FILTERS.some((f) => f.key === urlFilter) ? urlFilter! : "all";
  const setFilter = (key: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key === "all") next.delete("filter");
        else next.set("filter", key);
        return next;
      },
      { replace: true },
    );
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const prefilledCustomerId = params.get("newFor") ?? "";
  useEffect(() => {
    if (prefilledCustomerId) setCreateOpen(true);
  }, [prefilledCustomerId]);

  const events = data ?? [];
  const filtered = useMemo(() => {
    if (filter === "all") return events;
    if (filter === "open") return events.filter((e) => OPEN.has(e.status));
    if (filter === "won") return events.filter((e) => CONFIRMED.has(e.status));
    return events.filter((e) => e.status === filter);
  }, [events, filter]);

  const filterCounts = useMemo(() => {
    const m: Record<string, number> = { all: events.length };
    for (const f of FILTERS) {
      if (f.key === "all") continue;
      m[f.key] =
        f.key === "open"
          ? events.filter((e) => OPEN.has(e.status)).length
          : f.key === "won"
            ? events.filter((e) => CONFIRMED.has(e.status)).length
            : events.filter((e) => e.status === f.key).length;
    }
    return m;
  }, [events]);

  const customerName = useMemo(() => {
    const m = new Map((customersQ.data ?? []).map((c) => [c.id, c.name]));
    return (id: string) => m.get(id) ?? "–";
  }, [customersQ.data]);

  const totalValueCents = useMemo(() => events.reduce((s, e) => s + (e.amountTotalCents ?? 0), 0), [events]);
  const confirmedTotalCents = useMemo(
    () => events.filter((e) => CONFIRMED.has(e.status)).reduce((s, e) => s + (e.amountTotalCents ?? 0), 0),
    [events],
  );
  const dateRangeLabel = useMemo(() => {
    const dates = events.map((e) => e.eventDate).filter((d): d is string => Boolean(d)).sort();
    if (dates.length === 0) return null;
    const first = new Date(dates[0]!).toLocaleDateString("de-DE", { month: "long" });
    const last = new Date(dates[dates.length - 1]!).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
    return first === last.split(" ")[0] ? last : `${first} bis ${last}`;
  }, [events]);

  async function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setBusy(true);
    setFormError(null);
    try {
      await apiMutate("/api/events", "POST", {
        customerId: form.get("customerId"),
        occasion: (form.get("occasion") as string) || undefined,
        location: (form.get("location") as string) || undefined,
        eventDate: (form.get("eventDate") as string) || undefined,
        guestCount: form.get("guestCount") ? Number(form.get("guestCount")) : undefined,
      });
      setCreateOpen(false);
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Veranstaltungen"
        subtitle={
          events.length > 0
            ? `${events.length} Veranstaltung${events.length === 1 ? "" : "en"}${dateRangeLabel ? ` · ${dateRangeLabel}` : ""} · ${money(totalValueCents)} Gesamtwert`
            : "Alle Events – von der Anfrage bis zur Durchführung."
        }
        actions={
          <>
            {/* G1: Liste/Kalender-Umschalter als Segmented Control (vorher Outline-Link) */}
            <div className="flex items-center gap-0.5 rounded-[10px] border border-outline-variant/60 bg-surface-container-lowest p-[3px] dark:border-outline/40 dark:bg-inverse-surface">
              <span className="rounded-lg bg-primary px-3.5 py-1.5 font-label-md text-[12px] font-semibold text-on-primary">Liste</span>
              <Link
                to="/kalender"
                className="rounded-lg px-3.5 py-1.5 font-label-md text-[12px] font-semibold text-secondary-fixed-dim hover:text-on-surface dark:hover:text-inverse-on-surface"
              >
                Kalender
              </Link>
            </div>
            <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>
              <Icon name="add" className="text-[18px]" /> Veranstaltung anlegen
            </button>
          </>
        }
      />

      <div className="card overflow-hidden">
        {/* Filter-Tabs */}
        <div className="tab-scroll flex gap-1 border-b border-outline-variant/30 px-2 dark:border-outline/30">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`whitespace-nowrap border-b-2 px-4 py-3 font-label-md text-label-md transition-colors ${
                filter === f.key
                  ? "border-primary text-primary"
                  : "border-transparent text-secondary hover:text-on-surface dark:text-secondary-fixed-dim dark:hover:text-inverse-on-surface"
              }`}
            >
              {f.label} <span className="text-on-surface-variant/60">{filterCounts[f.key] ?? 0}</span>
            </button>
          ))}
        </div>

        {error && <ErrorNote message={error} onRetry={reload} />}

        {loading ? (
          <LoadingRows />
        ) : filtered.length === 0 ? (
          <EmptyState icon="event_busy" title="Keine Veranstaltungen in dieser Ansicht" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] border-collapse text-left">
                <thead>
                  <tr>
                    <th className="table-header">Veranstaltung</th>
                    <th className="table-header">Datum</th>
                    <th className="table-header">Kunde</th>
                    <th className="table-header">Gäste</th>
                    <th className="table-header">Ort</th>
                    <th className="table-header">Status</th>
                    <th className="table-header text-right">Wert</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e) => {
                    const cancelled = e.status === "cancelled";
                    return (
                      // CX-Audit 2026-07-17: GANZE Zeile navigiert zum Event-Detail (wie in
                      // kunden.tsx) — vorher war nur der Titel-Text ein Link, der Rest der
                      // Zeile eine tote Fläche und die Liste wirkte wie eine Sackgasse.
                      <tr
                        key={e.id}
                        onClick={() => navigate(`/veranstaltungen/${e.id}`)}
                        className="group cursor-pointer transition-colors hover:bg-surface-container-high/20 dark:hover:bg-surface-variant/20"
                      >
                        <td className="border-b border-outline-variant/10 px-4 py-4 dark:border-outline/10">
                          <Link
                            to={`/veranstaltungen/${e.id}`}
                            onClick={(ev) => ev.stopPropagation()}
                            className={`font-body-md text-body-md font-semibold group-hover:text-primary ${cancelled ? "text-secondary line-through dark:text-secondary-fixed-dim" : "text-on-background dark:text-inverse-on-surface"}`}
                          >
                            {e.occasion ?? e.number ?? "Event"}
                          </Link>
                          {e.number && (
                            <p className="font-body-sm text-body-sm text-on-surface-variant/70">
                              {e.number}
                            </p>
                          )}
                        </td>
                        <td className={`whitespace-nowrap border-b border-outline-variant/10 px-4 py-4 font-body-md text-body-md dark:border-outline/10 ${cancelled ? "text-secondary-fixed-dim" : "text-on-surface-variant"}`}>
                          {formatDate(e.eventDate)}
                        </td>
                        <td className={`border-b border-outline-variant/10 px-4 py-4 font-body-md text-body-md dark:border-outline/10 ${cancelled ? "text-secondary-fixed-dim" : "text-on-surface-variant"}`}>
                          {customerName(e.customerId)}
                        </td>
                        <td className={`border-b border-outline-variant/10 px-4 py-4 font-body-md text-body-md dark:border-outline/10 ${cancelled ? "text-secondary-fixed-dim" : "text-on-surface-variant"}`}>
                          {e.guestCount ?? "–"}
                        </td>
                        <td className={`max-w-[180px] truncate border-b border-outline-variant/10 px-4 py-4 font-body-md text-body-md dark:border-outline/10 ${cancelled ? "text-secondary-fixed-dim" : "text-on-surface-variant"}`}>
                          {e.location ?? "Ort noch offen"}
                        </td>
                        <td className="border-b border-outline-variant/10 px-4 py-4 dark:border-outline/10">
                          <StatusPill meta={EVENT_STATUS[e.status]} />
                        </td>
                        <td className={`border-b border-outline-variant/10 px-4 py-4 text-right font-body-md text-body-md font-semibold dark:border-outline/10 ${cancelled ? "text-secondary-fixed-dim" : "text-on-background dark:text-inverse-on-surface"}`}>
                          {cancelled ? "—" : money(e.amountTotalCents)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-outline-variant/30 px-4 py-3 dark:border-outline/30">
              <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                {filtered.length} von {events.length} Veranstaltung{events.length === 1 ? "" : "en"}
              </span>
              <span className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                Bestätigt gesamt: {money(confirmedTotalCents)}
              </span>
            </div>
          </>
        )}
      </div>

      <Drawer open={createOpen} onClose={() => setCreateOpen(false)} title="Event anlegen">
        <form className="space-y-4" onSubmit={(e) => void handleCreate(e)}>
          {formError && <ErrorNote message={formError} />}
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              Kunde *
            </span>
            {/* A-03: key an den Ladezustand koppeln, damit defaultValue={prefilledCustomerId}
                greift, sobald die Kundenliste async nachgeladen ist (sonst bleibt die
                Vorauswahl aus ?newFor= leer und ein falscher Kunde wird riskiert). */}
            <select key={customersQ.data ? "cust-ready" : "cust-loading"} name="customerId" required className="input" defaultValue={prefilledCustomerId}>
              <option value="">– Kunde wählen –</option>
              {(customersQ.data ?? []).map((cu) => (
                <option key={cu.id} value={cu.id}>
                  {cu.name}
                  {cu.company ? ` (${cu.company})` : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              Anlass
            </span>
            <input name="occasion" className="input" placeholder="z. B. Sommerfest" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
                Datum
              </span>
              <input name="eventDate" type="date" className="input" />
            </label>
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
                Gäste
              </span>
              <input name="guestCount" type="number" min="0" className="input" placeholder="80" />
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              Ort
            </span>
            <input name="location" className="input" placeholder="z. B. Villa Mignon, Hamburg" />
          </label>
          <div className="flex justify-end gap-3 border-t border-outline-variant/30 pt-4 dark:border-outline/30">
            <button type="button" className="btn-secondary" onClick={() => setCreateOpen(false)}>
              Abbrechen
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? "Speichert …" : "Event anlegen"}
            </button>
          </div>
        </form>
      </Drawer>
    </>
  );
}
